/**
 * The rules about what is *allowed*, sitting above a `Storage` that only reads
 * and writes rows.
 *
 * These live in core rather than in React components so the mobile app inherits
 * the behaviour instead of reimplementing it — and so the one rule that most
 * needs enforcing cannot be bypassed by a caller who is in a hurry.
 */

import type { Clock } from '../ports/clock.js'
import type { IdGenerator } from '../ports/id-generator.js'
import type { EpochRange, Storage } from '../ports/storage.js'
import {
  makeEntry,
  reviseEntry,
  validateEntry,
  validateTag,
  type Entry,
  type EntryChanges,
  type Measurement,
  type NewEntry,
  type Tag,
} from '../model/entry.js'
import { type PaletteColour } from '../model/palette.js'
import { err, invalid, ok, type Result } from '../model/result.js'
import { validateTemplate, type CheckInTemplate } from '../model/template.js'
import { dayKey, type Timestamp } from '../model/timestamp.js'
import {
  chosenOptionIds,
  isEnumKind,
  isMultiKind,
  isNumericKind,
  quantify,
  validateTrack,
  type EnumKind,
  type MultiKind,
  type Track,
  type TrackKind,
  type TrackValue,
} from '../model/track.js'
import { SPARKLINE_POINTS } from '../analysis/sparkline.js'
import { ReminderRepository } from './reminders.js'

export interface RepositoryDeps {
  readonly storage: Storage
  readonly clock: Clock
  readonly ids: IdGenerator
}

// ─── Tracks ───────────────────────────────────────────────────────────────────

export interface NewTrack {
  readonly name: string
  readonly kind: TrackKind
  readonly unit?: string
  readonly description?: string
  /** Absent is normal — `questionFor` derives one from the name (T-810). */
  readonly question?: string
  /** Absent is normal — `colourOfTrack` derives one from the id. */
  readonly colour?: PaletteColour
  readonly group?: string
}

/**
 * What deleting a track would actually do.
 *
 * Surfaced so the UI can say which before asking for confirmation. A dialog
 * saying "Delete?" that then archives is a broken promise, and this app exists
 * to produce evidence — evidence a single mis-tap can shred is not evidence
 * (ADR-0003).
 */
export type RemovalPlan =
  | { readonly kind: 'delete'; readonly track: Track }
  | { readonly kind: 'archive'; readonly track: Track; readonly measurementCount: number }

/**
 * A reason a proposed edit would damage existing history.
 *
 * These are not validation errors — the track being described is perfectly
 * valid in isolation. They exist because it *already has measurements*, and
 * applying the change would strand or misrepresent them.
 */
export type TrackChangeIssue =
  /**
   * The value type cannot change once anything has been recorded.
   *
   * A boolean measurement in a numeric track is not convertible into anything
   * meaningful, and guessing would silently rewrite what the user actually
   * observed.
   */
  | { readonly kind: 'valueTypeLocked'; readonly measurementCount: number }
  /**
   * Narrowing a numeric range past existing data.
   *
   * The measurements would still be stored, but every chart and average would
   * be reading values the track now claims are impossible.
   */
  | {
      readonly kind: 'rangeExcludesData'
      readonly affected: number
      readonly lowest: number
      readonly highest: number
    }
  /**
   * Removing an option that measurements point at. Archive it instead.
   *
   * Any option list, not only an enum's — a multi-select's options are chosen
   * several at a time and are exactly as load-bearing (T-1301). The name keeps
   * the `enum` it was born with for the same reason the validation codes do:
   * it is what callers already match on, and a rename buys nothing the doc
   * comment cannot say.
   */
  | {
      readonly kind: 'enumOptionRecorded'
      readonly optionId: string
      readonly label: string
      readonly affected: number
    }

/**
 * What a proposed edit would cost, before it is applied.
 *
 * The same shape as `RemovalPlan` and for the same reason: an app that accepts
 * an edit and then quietly does something else is not trustworthy, and this is
 * the point at which trust matters. `blocked` being empty means the change is
 * safe to apply as described.
 */
export interface TrackChangePlan {
  readonly measurementCount: number
  readonly blocked: readonly TrackChangeIssue[]
}

/**
 * An edit, where `undefined` means **clear this field**.
 *
 * `Partial<Track>` cannot express that under `exactOptionalPropertyTypes`: the
 * bare optional form rejects an explicit `undefined`, so there would be no way
 * to remove a unit once one had been set. Merging strips undefined-valued keys
 * rather than storing them, so the stored track never carries a present-but-
 * undefined field.
 */
export type TrackChanges = {
  readonly [K in keyof Omit<Track, 'id' | 'createdAt'>]?: Track[K] | undefined
}

/** A track with the numbers a list needs, gathered in one pass. */
export interface TrackSummary {
  readonly track: Track
  readonly measurementCount: number
  readonly firstAt?: Timestamp
  readonly lastAt?: Timestamp
  /**
   * The most recent readings, oldest first, for a sparkline (T-907).
   *
   * Quantified, so a track that cannot produce a number — an unordered enum —
   * carries an empty list rather than a shape that would imply an order its
   * options do not have. The most recent *readings*, not the last seven days:
   * two tracks' sparklines are therefore not comparable in time, which is why
   * the row also carries a count in words.
   */
  readonly recent: readonly number[]
}

/**
 * A blocking issue, as a validation error the UI can render.
 *
 * Two representations of the same fact, deliberately. `TrackChangePlan` carries
 * the numbers so a confirmation dialog can compose its own sentence; this is
 * for the case where an edit was attempted anyway and the form needs something
 * to show. Neither is derived from the other's text, so neither can drift into
 * saying something the plan does not support.
 */
export function describeChangeIssue(issue: TrackChangeIssue) {
  switch (issue.kind) {
    case 'valueTypeLocked':
      return invalid(
        'track.kind.locked',
        `This track already has ${String(issue.measurementCount)} measurements, so its value type cannot change. Create a new track instead.`,
        'kind',
      )
    case 'rangeExcludesData':
      return invalid(
        'track.range.excludesData',
        `${String(issue.affected)} recorded measurements fall outside that range — the values run from ${String(issue.lowest)} to ${String(issue.highest)}.`,
        'kind',
      )
    case 'enumOptionRecorded':
      return invalid(
        'track.enum.option.recorded',
        `"${issue.label}" has been recorded ${String(issue.affected)} times and cannot be deleted. Archive it instead to keep that history readable.`,
        'kind.options',
      )
  }
}

/** The two kinds that carry an option list, which the recorded-option guard treats alike. */
const hasOptionList = (kind: TrackKind): kind is EnumKind | MultiKind =>
  isEnumKind(kind) || isMultiKind(kind)

/** The changed keys that actually carry a value. */
function stripUndefined(changes: TrackChanges): Partial<Track> {
  return Object.fromEntries(
    Object.entries(changes).filter(([, value]) => value !== undefined),
  )
}

export class TrackRepository {
  constructor(private readonly deps: RepositoryDeps) {}

  get(id: string): Promise<Track | undefined> {
    return this.deps.storage.tracks.get(id)
  }

  async list(options: { includeArchived?: boolean } = {}): Promise<Track[]> {
    const all = await this.deps.storage.tracks.list()
    const visible =
      options.includeArchived === true ? all : all.filter((t) => !t.archived)
    return visible.sort((a, b) => a.position - b.position)
  }

  async create(input: NewTrack): Promise<Result<Track>> {
    const now = this.deps.clock.now()
    const existing = await this.deps.storage.tracks.list()
    const track: Track = {
      id: this.deps.ids.next(),
      name: input.name,
      ...(input.unit === undefined ? {} : { unit: input.unit }),
      ...(input.description === undefined ? {} : { description: input.description }),
      ...(input.question === undefined ? {} : { question: input.question }),
      ...(input.colour === undefined ? {} : { colour: input.colour }),
      ...(input.group === undefined ? {} : { group: input.group.trim() }),
      kind: input.kind,
      archived: false,
      position: existing.length,
      createdAt: now,
      updatedAt: now,
    }

    const validated = validateTrack(track, { existingTracks: existing })
    if (!validated.ok) {
      return validated
    }
    await this.deps.storage.tracks.put(track)
    return ok(track)
  }

  async update(id: string, changes: TrackChanges): Promise<Result<Track>> {
    const current = await this.deps.storage.tracks.get(id)
    if (current === undefined) {
      return err<Track>(invalid('track.notFound', 'That track no longer exists.'))
    }

    // Explicit `undefined` means clear, so those keys are dropped rather than
    // stored — a present-but-undefined `unit` round-trips through IndexedDB and
    // comes back as a field that exists and holds nothing.
    const merged: Record<string, unknown> = {
      ...current,
      ...stripUndefined(changes),
    }
    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined) {
        delete merged[key]
      }
    }

    const updated = {
      ...merged,
      id: current.id,
      createdAt: current.createdAt,
      updatedAt: this.deps.clock.now(),
    } as Track
    const existing = await this.deps.storage.tracks.list()
    const validated = validateTrack(updated, { existingTracks: existing })
    if (!validated.ok) {
      return validated
    }
    await this.deps.storage.tracks.put(updated)
    return ok(updated)
  }

  archive(id: string): Promise<Result<Track>> {
    return this.update(id, { archived: true })
  }

  restore(id: string): Promise<Result<Track>> {
    return this.update(id, { archived: false })
  }

  /** How many entries measured this track. */
  async measurementCount(id: string): Promise<number> {
    return (await this.deps.storage.entries.listByTrack(id)).length
  }

  /**
   * Count and date range per track, for a list that shows both.
   *
   * One pass over the entries rather than three queries per track: a list of
   * twenty tracks would otherwise issue sixty, and this runs on every visit to
   * the screen.
   */
  async summaries(options: { includeArchived?: boolean } = {}): Promise<TrackSummary[]> {
    const tracks = await this.list(options)
    const entries = await this.deps.storage.entries.list()

    const counts = new Map<string, { count: number; first: Timestamp; last: Timestamp }>()
    const readings = new Map<string, { at: number; value: TrackValue }[]>()
    for (const entry of entries) {
      for (const measurement of entry.measurements) {
        const found = readings.get(measurement.trackId)
        const reading = { at: entry.at.epochMs, value: measurement.value }
        if (found === undefined) {
          readings.set(measurement.trackId, [reading])
        } else {
          found.push(reading)
        }

        const seen = counts.get(measurement.trackId)
        if (seen === undefined) {
          counts.set(measurement.trackId, { count: 1, first: entry.at, last: entry.at })
          continue
        }
        seen.count += 1
        if (entry.at.epochMs < seen.first.epochMs) {
          seen.first = entry.at
        }
        if (entry.at.epochMs > seen.last.epochMs) {
          seen.last = entry.at
        }
      }
    }

    return tracks.map((track) => {
      const seen = counts.get(track.id)
      /*
       * Sorted here rather than assumed: `storage.entries.list()` makes no
       * ordering promise, and a sparkline drawn from insertion order is a
       * different shape from the one the chart shows for the same readings.
       */
      const recent = (readings.get(track.id) ?? [])
        .sort((a, b) => a.at - b.at)
        .slice(-SPARKLINE_POINTS)
        .map((reading) => quantify(track, reading.value))
        .filter((value): value is number => value !== null)

      return {
        track,
        measurementCount: seen?.count ?? 0,
        ...(seen === undefined ? {} : { firstAt: seen.first, lastAt: seen.last }),
        recent,
      }
    })
  }

  /**
   * What a proposed edit would cost, without applying it.
   *
   * The same shape as `planRemoval`, for the same reason: the UI has to be able
   * to say what will happen *before* the user commits. `blocked` empty means
   * safe to apply.
   *
   * Nothing here is about validity — the proposed track is well-formed. These
   * are the ways a well-formed change damages measurements that already exist,
   * and they are only discoverable by looking at the history.
   */
  async planUpdate(id: string, changes: TrackChanges): Promise<Result<TrackChangePlan>> {
    const current = await this.deps.storage.tracks.get(id)
    if (current === undefined) {
      return err<TrackChangePlan>(
        invalid('track.notFound', 'That track no longer exists.'),
      )
    }

    const entries = await this.deps.storage.entries.listByTrack(id)
    const values = entries.flatMap((entry) =>
      entry.measurements.filter((m) => m.trackId === id).map((m) => m.value),
    )
    const plan: TrackChangeIssue[] = []

    // Nothing recorded means nothing to protect, and the editor should be
    // completely unrestricted.
    if (values.length === 0) {
      return ok({ measurementCount: 0, blocked: [] })
    }

    const next = changes.kind
    if (next !== undefined && next.kind !== current.kind.kind) {
      plan.push({ kind: 'valueTypeLocked', measurementCount: values.length })
      // No point reporting range or option problems against a type that cannot
      // be adopted anyway — it would be three errors describing one refusal.
      return ok({ measurementCount: values.length, blocked: plan })
    }

    if (next !== undefined && isNumericKind(next)) {
      const numbers = values.filter((value): value is number => typeof value === 'number')
      const outside = numbers.filter((value) => value < next.min || value > next.max)
      if (outside.length > 0) {
        plan.push({
          kind: 'rangeExcludesData',
          affected: outside.length,
          lowest: Math.min(...numbers),
          highest: Math.max(...numbers),
        })
      }
    }

    /*
     * Both option-list kinds, not just enums. The type-change branch above has
     * already returned unless `next` and `current` are the same kind, so this
     * pair is always two enums or two multi-selects.
     *
     * A multi-select records several ids per measurement, and counting them
     * one value at a time is what would have left every option in one
     * unprotected — deletable while entries still pointed at it.
     */
    if (next !== undefined && hasOptionList(next) && hasOptionList(current.kind)) {
      const keptIds = new Set(next.options.map((option) => option.id))
      const recorded = new Map<string, number>()
      for (const value of values) {
        for (const optionId of chosenOptionIds(value)) {
          recorded.set(optionId, (recorded.get(optionId) ?? 0) + 1)
        }
      }
      for (const [optionId, affected] of recorded) {
        if (!keptIds.has(optionId)) {
          // The label comes from the *current* configuration: the option being
          // removed is not in the proposed one, and "option abc123 is in use"
          // is not something a user can act on.
          const label =
            current.kind.options.find((option) => option.id === optionId)?.label ??
            optionId
          plan.push({ kind: 'enumOptionRecorded', optionId, label, affected })
        }
      }
    }

    return ok({ measurementCount: values.length, blocked: plan })
  }

  /**
   * Applies an edit, refusing one that would damage history.
   *
   * `update` stays unguarded because archive, restore and reorder all go
   * through it and none of them touch the configuration. This is the path the
   * editor uses, and the guard is here rather than there so that a caller has
   * to opt into the check by choosing the method that performs it — the same
   * split as `remove` and `planRemoval`.
   */
  async applyEdit(id: string, changes: TrackChanges): Promise<Result<Track>> {
    const planned = await this.planUpdate(id, changes)
    if (!planned.ok) {
      return err<Track>(...planned.errors)
    }
    if (planned.value.blocked.length > 0) {
      return err<Track>(
        ...planned.value.blocked.map((issue) => describeChangeIssue(issue)),
      )
    }
    return this.update(id, changes)
  }

  /** What `remove` would do, so the UI can say so before asking. */
  async planRemoval(id: string): Promise<Result<RemovalPlan>> {
    const track = await this.deps.storage.tracks.get(id)
    if (track === undefined) {
      return err<RemovalPlan>(invalid('track.notFound', 'That track no longer exists.'))
    }
    const count = await this.measurementCount(id)
    return ok(
      count === 0
        ? { kind: 'delete', track }
        : { kind: 'archive', track, measurementCount: count },
    )
  }

  /**
   * Deletes a track only when nothing was ever recorded against it.
   *
   * Otherwise it archives, and says so. Deleting a track must never destroy the
   * measurements taken against it — that is the rule most likely to be
   * "simplified" later by someone who finds it surprising.
   */
  async remove(id: string): Promise<Result<RemovalPlan>> {
    const plan = await this.planRemoval(id)
    if (!plan.ok) {
      return plan
    }

    if (plan.value.kind === 'delete') {
      await this.deps.storage.tracks.remove(id)
      return plan
    }

    const archived = await this.archive(id)
    return archived.ok ? plan : err<RemovalPlan>(...archived.errors)
  }

  async reorder(orderedIds: readonly string[]): Promise<Result<Track[]>> {
    const tracks = await this.deps.storage.tracks.list()
    const byId = new Map(tracks.map((track) => [track.id, track]))
    const now = this.deps.clock.now()
    const reordered: Track[] = []

    for (const [index, id] of orderedIds.entries()) {
      const track = byId.get(id)
      if (track === undefined) {
        return err<Track[]>(invalid('track.notFound', 'That track no longer exists.'))
      }
      reordered.push({ ...track, position: index, updatedAt: now })
    }

    await this.deps.storage.tracks.putMany(reordered)
    return ok(reordered)
  }
}

/** Filters for `EntryRepository.find`. Omitted fields do not narrow. */
export interface EntryQuery {
  readonly range?: EpochRange
  readonly trackId?: string
  readonly tagId?: string
  readonly templateId?: string
}

// ─── Entries ──────────────────────────────────────────────────────────────────

export class EntryRepository {
  constructor(private readonly deps: RepositoryDeps) {}

  get(id: string): Promise<Entry | undefined> {
    return this.deps.storage.entries.get(id)
  }

  listByRange(range: EpochRange): Promise<Entry[]> {
    return this.deps.storage.entries.listByRange(range)
  }

  listByTrack(trackId: string, range?: EpochRange): Promise<Entry[]> {
    return this.deps.storage.entries.listByTrack(trackId, range)
  }

  listByTag(tagId: string, range?: EpochRange): Promise<Entry[]> {
    return this.deps.storage.entries.listByTag(tagId, range)
  }

  listByTemplate(templateId: string, range?: EpochRange): Promise<Entry[]> {
    return this.deps.storage.entries.listByTemplate(templateId, range)
  }

  private async context(): Promise<{ tracks: Track[]; tags: Tag[] }> {
    const [tracks, tags] = await Promise.all([
      this.deps.storage.tracks.list(),
      this.deps.storage.tags.list(),
    ])
    return { tracks, tags }
  }

  async create(input: Omit<NewEntry, 'id'>): Promise<Result<Entry>> {
    const entry = makeEntry({ ...input, id: this.deps.ids.next() }, this.deps.clock)
    const validated = validateEntry(entry, await this.context())
    if (!validated.ok) {
      return validated
    }
    await this.deps.storage.entries.put(entry)
    return ok(entry)
  }

  async update(id: string, changes: EntryChanges): Promise<Result<Entry>> {
    const current = await this.deps.storage.entries.get(id)
    if (current === undefined) {
      return err<Entry>(invalid('entry.notFound', 'That entry no longer exists.'))
    }

    const revised = reviseEntry(current, changes, this.deps.clock)
    const validated = validateEntry(revised, await this.context())
    if (!validated.ok) {
      return validated
    }
    await this.deps.storage.entries.put(revised)
    return ok(revised)
  }

  async remove(id: string): Promise<Result<Entry>> {
    const current = await this.deps.storage.entries.get(id)
    if (current === undefined) {
      return err<Entry>(invalid('entry.notFound', 'That entry no longer exists.'))
    }
    await this.deps.storage.entries.remove(id)
    return ok(current)
  }

  /** Restores a deleted entry exactly, id included, for an undo. */
  async restore(entry: Entry): Promise<Result<Entry>> {
    const validated = validateEntry(entry, await this.context())
    if (!validated.ok) {
      return validated
    }
    await this.deps.storage.entries.put(entry)
    return ok(entry)
  }

  /** Every entry, ascending by instant. */
  list(): Promise<Entry[]> {
    return this.deps.storage.entries.list()
  }

  /**
   * Entries matching every supplied filter, ascending by instant.
   *
   * Filters compose by intersection: a track *and* a tag *and* a range means
   * all three, not any of them. The composition lives here rather than in a
   * timeline component because the native app needs the same behaviour, and
   * because getting "and" versus "or" wrong is invisible until someone reads a
   * report built on it.
   *
   * The most selective indexed filter runs first and the rest narrow in
   * memory. Track and tag are far more selective than a date range in normal
   * use — a year of entries against one track is a fraction of a year of
   * entries — so seeking those first means the in-memory pass runs over tens of
   * rows rather than thousands.
   */
  async find(query: EntryQuery = {}): Promise<Entry[]> {
    const { range, trackId, tagId, templateId } = query

    const seeded =
      trackId !== undefined
        ? await this.deps.storage.entries.listByTrack(trackId)
        : tagId !== undefined
          ? await this.deps.storage.entries.listByTag(tagId)
          : templateId !== undefined
            ? await this.deps.storage.entries.listByTemplate(templateId)
            : range !== undefined
              ? await this.deps.storage.entries.listByRange(range)
              : await this.deps.storage.entries.list()

    return seeded.filter(
      (entry) =>
        (range === undefined ||
          (entry.at.epochMs >= range.fromEpochMs &&
            entry.at.epochMs <= range.toEpochMs)) &&
        (trackId === undefined ||
          entry.measurements.some((m) => m.trackId === trackId)) &&
        (tagId === undefined || entry.tagIds.includes(tagId)) &&
        (templateId === undefined || entry.templateId === templateId),
    )
  }

  /**
   * The first and last entry ever recorded, for resolving "all time".
   *
   * Both absent on an empty database, which is not an error — `resolveRange`
   * takes that as "there is nothing yet" and produces an ordinary range that
   * happens to contain no data.
   */
  async bounds(): Promise<{ first?: Entry; last?: Entry }> {
    // Two indexed reads, not a full scan. This runs on every page load, because
    // "all time" is the default range and every screen resolves it.
    const [first, last] = await Promise.all([
      this.deps.storage.entries.earliest(),
      this.deps.storage.entries.latest(),
    ])
    return {
      ...(first === undefined ? {} : { first }),
      ...(last === undefined ? {} : { last }),
    }
  }

  /** How many entries exist, without reading any of them. */
  count(): Promise<number> {
    return this.deps.storage.entries.count()
  }

  /**
   * The most recent entries, newest first.
   *
   * Newest-first is the opposite of everything else here, deliberately: charts
   * and exports read forwards, but "what did I just record" reads backwards,
   * and making each caller reverse it is how one of them forgets.
   *
   * Reads the whole collection and takes the tail. Fine at the scale this app
   * reaches, and the honest note is that a reverse cursor would be better —
   * that is a change to the `Storage` port, so it waits until the cost is real
   * rather than theoretical.
   */
  async recent(limit: number): Promise<Entry[]> {
    const all = await this.deps.storage.entries.list()
    return all.slice(-Math.max(0, limit)).reverse()
  }

  /**
   * Groups by local calendar day, using each entry's own captured zone.
   *
   * Not the reader's zone and not UTC: an entry recorded at 23:30 local belongs
   * to that day even though UTC has already rolled over (ADR-0006).
   */
  groupByDay(entries: readonly Entry[]): Map<string, Entry[]> {
    const days = new Map<string, Entry[]>()
    for (const entry of [...entries].sort((a, b) => a.at.epochMs - b.at.epochMs)) {
      const key = dayKey(entry.at)
      const bucket = days.get(key)
      if (bucket === undefined) {
        days.set(key, [entry])
      } else {
        bucket.push(entry)
      }
    }
    return days
  }

  /** The most recent measurement for a track, for prefilling a form. */
  async latestMeasurement(
    trackId: string,
  ): Promise<{ entry: Entry; measurement: Measurement } | undefined> {
    const entries = await this.deps.storage.entries.listByTrack(trackId)
    const latest = entries[entries.length - 1]
    if (latest === undefined) {
      return undefined
    }
    const measurement = latest.measurements.find((m) => m.trackId === trackId)
    return measurement === undefined ? undefined : { entry: latest, measurement }
  }
}

// ─── Tags ─────────────────────────────────────────────────────────────────────

export class TagRepository {
  constructor(private readonly deps: RepositoryDeps) {}

  list(): Promise<Tag[]> {
    return this.deps.storage.tags.list()
  }

  get(id: string): Promise<Tag | undefined> {
    return this.deps.storage.tags.get(id)
  }

  async create(name: string, colour?: string): Promise<Result<Tag>> {
    const tag: Tag = {
      id: this.deps.ids.next(),
      name,
      ...(colour === undefined ? {} : { colour }),
    }
    const validated = validateTag(tag, await this.deps.storage.tags.list())
    if (!validated.ok) {
      return validated
    }
    await this.deps.storage.tags.put(tag)
    return ok(tag)
  }

  async rename(id: string, name: string): Promise<Result<Tag>> {
    return this.update(id, { name })
  }

  /**
   * Changes a tag's name or colour.
   *
   * Neither touches an entry. Entries reference a tag by **id**, which is the
   * whole reason renaming is always safe — the alternative, storing labels on
   * entries, would make every rename a rewrite of history.
   */
  async update(
    id: string,
    changes: { readonly name?: string; readonly colour?: string | undefined },
  ): Promise<Result<Tag>> {
    const current = await this.deps.storage.tags.get(id)
    if (current === undefined) {
      return err<Tag>(invalid('tag.notFound', 'That tag no longer exists.'))
    }

    const updated: Tag = {
      ...current,
      ...(changes.name === undefined ? {} : { name: changes.name }),
    }
    if ('colour' in changes) {
      if (changes.colour === undefined) {
        delete (updated as { colour?: string }).colour
      } else {
        Object.assign(updated, { colour: changes.colour })
      }
    }

    const validated = validateTag(updated, await this.deps.storage.tags.list())
    if (!validated.ok) {
      return validated
    }
    await this.deps.storage.tags.put(updated)
    return ok(updated)
  }

  /**
   * Removes a tag and detaches it from every entry, deleting no entries.
   *
   * Losing an entry because a label was tidied up would be absurd, and the
   * whole point of this app is that history survives.
   */
  async remove(id: string): Promise<Result<{ detachedFrom: number }>> {
    const tag = await this.deps.storage.tags.get(id)
    if (tag === undefined) {
      return err<{ detachedFrom: number }>(
        invalid('tag.notFound', 'That tag no longer exists.'),
      )
    }

    return this.deps.storage.transaction(async (tx) => {
      const affected = await tx.entries.listByTag(id)
      const detached = affected.map((entry) => ({
        ...entry,
        tagIds: entry.tagIds.filter((tagId) => tagId !== id),
        updatedAt: this.deps.clock.now(),
      }))
      await tx.entries.putMany(detached)
      await tx.tags.remove(id)
      return ok({ detachedFrom: detached.length })
    })
  }

  /**
   * Folds one tag into another, for the inevitable "snack" and "snacks".
   *
   * Free-text tagging produces near-duplicates within weeks, and without this
   * the only remedy is re-tagging entries by hand.
   */
  async merge(fromId: string, intoId: string): Promise<Result<{ moved: number }>> {
    if (fromId === intoId) {
      return err<{ moved: number }>(invalid('tag.merge.same', 'Those are the same tag.'))
    }
    const [from, into] = await Promise.all([
      this.deps.storage.tags.get(fromId),
      this.deps.storage.tags.get(intoId),
    ])
    if (from === undefined || into === undefined) {
      return err<{ moved: number }>(invalid('tag.notFound', 'That tag no longer exists.'))
    }

    return this.deps.storage.transaction(async (tx) => {
      const affected = await tx.entries.listByTag(fromId)
      const moved = affected.map((entry) => ({
        ...entry,
        // A Set, so an entry already carrying both ends up with one, not two.
        tagIds: [...new Set(entry.tagIds.map((id) => (id === fromId ? intoId : id)))],
        updatedAt: this.deps.clock.now(),
      }))
      await tx.entries.putMany(moved)
      await tx.tags.remove(fromId)
      return ok({ moved: moved.length })
    })
  }

  async usageCounts(): Promise<Map<string, number>> {
    const entries = await this.deps.storage.entries.list()
    const counts = new Map<string, number>()
    for (const entry of entries) {
      for (const tagId of entry.tagIds) {
        counts.set(tagId, (counts.get(tagId) ?? 0) + 1)
      }
    }
    return counts
  }
}

// ─── Templates ────────────────────────────────────────────────────────────────

export interface NewTemplate {
  readonly name: string
  readonly trackIds: readonly string[]
  readonly description?: string
  readonly defaultTagIds?: readonly string[]
}

export class TemplateRepository {
  constructor(private readonly deps: RepositoryDeps) {}

  get(id: string): Promise<CheckInTemplate | undefined> {
    return this.deps.storage.templates.get(id)
  }

  async list(options: { includeArchived?: boolean } = {}): Promise<CheckInTemplate[]> {
    const all = await this.deps.storage.templates.list()
    const visible =
      options.includeArchived === true ? all : all.filter((t) => !t.archived)
    return visible.sort((a, b) => a.position - b.position)
  }

  /**
   * How many entries each template has produced, keyed by template id.
   *
   * Entries, not measurements: one check-in of eight tracks is one thing that
   * happened, and counting its measurements would make a long check-in look
   * eight times more used than a short one.
   *
   * Templates with none are absent rather than present at zero, the same shape
   * `usageCounts` uses — a caller reading a missing key as zero is deliberate,
   * and it keeps the map proportional to what exists.
   */
  async countsByTemplate(): Promise<Map<string, number>> {
    const entries = await this.deps.storage.entries.list()
    const counts = new Map<string, number>()
    for (const entry of entries) {
      if (entry.templateId === undefined) {
        continue
      }
      counts.set(entry.templateId, (counts.get(entry.templateId) ?? 0) + 1)
    }
    return counts
  }

  async create(input: NewTemplate): Promise<Result<CheckInTemplate>> {
    const now = this.deps.clock.now()
    const [existing, tracks] = await Promise.all([
      this.deps.storage.templates.list(),
      this.deps.storage.tracks.list(),
    ])

    const template: CheckInTemplate = {
      id: this.deps.ids.next(),
      name: input.name,
      ...(input.description === undefined ? {} : { description: input.description }),
      trackIds: input.trackIds,
      defaultTagIds: input.defaultTagIds ?? [],
      position: existing.length,
      archived: false,
      createdAt: now,
      updatedAt: now,
    }

    const validated = validateTemplate(template, { existingTemplates: existing, tracks })
    if (!validated.ok) {
      return validated
    }
    await this.deps.storage.templates.put(template)
    return ok(template)
  }

  async update(
    id: string,
    changes: Partial<Omit<CheckInTemplate, 'id' | 'createdAt'>>,
  ): Promise<Result<CheckInTemplate>> {
    const current = await this.deps.storage.templates.get(id)
    if (current === undefined) {
      return err<CheckInTemplate>(
        invalid('template.notFound', 'That check-in no longer exists.'),
      )
    }

    const updated: CheckInTemplate = {
      ...current,
      ...changes,
      id: current.id,
      createdAt: current.createdAt,
      updatedAt: this.deps.clock.now(),
    }
    const [existing, tracks] = await Promise.all([
      this.deps.storage.templates.list(),
      this.deps.storage.tracks.list(),
    ])
    const validated = validateTemplate(updated, { existingTemplates: existing, tracks })
    if (!validated.ok) {
      return validated
    }
    await this.deps.storage.templates.put(updated)
    return ok(updated)
  }

  /** Copies a template under a new name, since variants differ by a track or two. */
  async duplicate(id: string, name: string): Promise<Result<CheckInTemplate>> {
    const source = await this.deps.storage.templates.get(id)
    if (source === undefined) {
      return err<CheckInTemplate>(
        invalid('template.notFound', 'That check-in no longer exists.'),
      )
    }
    return this.create({
      name,
      trackIds: source.trackIds,
      ...(source.description === undefined ? {} : { description: source.description }),
      defaultTagIds: source.defaultTagIds,
    })
  }

  archive(id: string): Promise<Result<CheckInTemplate>> {
    return this.update(id, { archived: true })
  }

  /**
   * Deletes a template outright.
   *
   * Unlike a track this destroys nothing: entries keep their `templateId`, so
   * past check-ins remain attributable even once the check-in itself is gone.
   */
  async remove(id: string): Promise<Result<CheckInTemplate>> {
    const template = await this.deps.storage.templates.get(id)
    if (template === undefined) {
      return err<CheckInTemplate>(
        invalid('template.notFound', 'That check-in no longer exists.'),
      )
    }
    await this.deps.storage.templates.remove(id)
    return ok(template)
  }

  async reorder(orderedIds: readonly string[]): Promise<Result<CheckInTemplate[]>> {
    const templates = await this.deps.storage.templates.list()
    const byId = new Map(templates.map((template) => [template.id, template]))
    const now = this.deps.clock.now()
    const reordered: CheckInTemplate[] = []

    for (const [index, id] of orderedIds.entries()) {
      const template = byId.get(id)
      if (template === undefined) {
        return err<CheckInTemplate[]>(
          invalid('template.notFound', 'That check-in no longer exists.'),
        )
      }
      reordered.push({ ...template, position: index, updatedAt: now })
    }

    await this.deps.storage.templates.putMany(reordered)
    return ok(reordered)
  }
}

/** Everything the app talks to, built from one set of dependencies. */
export interface Repositories {
  readonly tracks: TrackRepository
  readonly entries: EntryRepository
  readonly tags: TagRepository
  readonly templates: TemplateRepository
  /**
   * Reminder schedules (T-701).
   *
   * Alongside the others despite being stored differently — as one value in the
   * settings store rather than a collection. That is a storage decision, and no
   * screen should have to know about it.
   */
  readonly reminders: ReminderRepository
}

export function createRepositories(deps: RepositoryDeps): Repositories {
  return {
    tracks: new TrackRepository(deps),
    entries: new EntryRepository(deps),
    tags: new TagRepository(deps),
    templates: new TemplateRepository(deps),
    reminders: new ReminderRepository(deps),
  }
}
